import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import { useDarkMode } from "../../../context/DarkModeProvider";
import "../../../assets/css/gamesCss/Array/varietyofarrays.css";
import "../../../assets/css/colors.css";
import { faL } from "@fortawesome/free-solid-svg-icons";
import { useLanguage } from "../../../context/LanguageProvider";


const VarietyOfArrays = () => {
    const { darkMode } = useDarkMode();
    const [currentLevel, setCurrentLevel] = useState(0);
    const [matrix, setMatrix] = useState([]);
    const [lives, setLives] = useState(3);
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const [message, setMessage] = useState("");
    const [isCorrect, setIsCorrect] = useState(false);
    const [selectedCell, setSelectedCell] = useState(null);
    const [foundErrors, setFoundErrors] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [showHint, setShowHint] = useState(false);
    const { selLanguage, toggleLanguage, isLoading } = useLanguage();

    const LEVELS = [
    // PHASE 1: Puzzle Mode - Find Errors
    {
        id: 1,
        phase: 1,
        type: "diagonal",
        title: selLanguage
            ? "Εύρεση Λαθών σε Διαγώνιο Πίνακα"
            : "Find Errors in Diagonal Matrix",
        description: selLanguage
            ? "Βρες τα λάθη στον διαγώνιο πίνακα."
            : "Identify the errors in the diagonal matrix.",
        matrix: [
            [8, 0, 0, 0, 0],
            [0, 5, 0, 3, 0],
            [0, 0, 12, 0, 0],
            [0, 0, 0, 7, 0],
            [0, 2, 0, 0, 15]
        ],
        errors: [
            {
                row: 1,
                col: 3,
                reason: selLanguage
                    ? "Πρέπει να είναι 0 (εκτός διαγωνίου)"
                    : "Must be 0 (off the diagonal)"
            },
            {
                row: 4,
                col: 1,
                reason: selLanguage
                    ? "Πρέπει να είναι 0 (εκτός διαγωνίου)"
                    : "Must be 0 (off the diagonal)"
            }
        ],
        correctMatrix: [
            [8, 0, 0, 0, 0],
            [0, 5, 0, 0, 0],
            [0, 0, 12, 0, 0],
            [0, 0, 0, 7, 0],
            [0, 0, 0, 0, 15]
        ]
    },

    {
        id: 2,
        phase: 1,
        type: "upper_triangular",
        title: selLanguage
            ? "Εύρεση Λαθών σε Άνω Τριγωνικό Πίνακα"
            : "Find Errors in Upper Triangular Matrix",
        description: selLanguage
            ? "Βρες τα λάθη στον άνω τριγωνικό πίνακα."
            : "Find the errors in the upper triangular matrix.",
        matrix: [
            [9, 4, 2, 1],
            [0, 8, 5, 3],
            [0, 6, 11, 7],
            [0, 0, 0, 13]
        ],
        errors: [
            {
                row: 2,
                col: 1,
                reason: selLanguage
                    ? "Πρέπει να είναι 0 (κάτω από διαγώνιο)"
                    : "Must be 0 (below diagonal)"
            }
        ],
        correctMatrix: [
            [9, 4, 2, 1],
            [0, 8, 5, 3],
            [0, 0, 11, 7],
            [0, 0, 0, 13]
        ]
    },

    // PHASE 2: Build the Matrix
    {
        id: 3,
        phase: 2,
        type: "diagonal",
        title: selLanguage
            ? "Κατασκεύασε Διαγώνιο Πίνακα 4×4"
            : "Construct a 4×4 Diagonal Matrix",
        description: selLanguage
            ? "Συμπλήρωσε τον πίνακα ώστε να γίνει διαγώνιος. Μόνο η κύρια διαγώνιος A[i][i] επιτρέπεται να έχει μη-μηδενικά στοιχεία!"
            : "Fill in the matrix to make it diagonal. Only the main diagonal A[i][i] may contain non-zero values!",
        size: 4,
        template: [
            [null, null, null, null],
            [null, null, null, null],
            [null, null, null, null],
            [null, null, null, null]
        ]
    },

    {
        id: 4,
        phase: 2,
        type: "lower_triangular",
        title: selLanguage
            ? "Κατασκεύασε Κάτω Τριγωνικό Πίνακα 4×4"
            : "Construct a 4×4 Lower Triangular Matrix",
        description: selLanguage
            ? "Δημιούργησε έναν κάτω τριγωνικό πίνακα. Όλα τα στοιχεία πάνω από τη διαγώνιο (i < j) πρέπει να είναι 0!"
            : "Create a lower triangular matrix. All elements above the diagonal (i < j) must be 0!",
        size: 4,
        template: [
            [null, null, null, null],
            [null, null, null, null],
            [null, null, null, null],
            [null, null, null, null]
        ]
    },

    // PHASE 3: Mirror Challenge
    {
        id: 5,
        phase: 3,
        type: "symmetric",
        title: selLanguage
            ? "Πρόκληση Συμμετρίας 4×4"
            : "4×4 Symmetry Challenge",
        description: selLanguage
            ? "Συμπλήρωσε τα κενά ώστε ο πίνακας να γίνει συμμετρικός. Θυμήσου: A[i][j] = A[j][i] για κάθε θέση!"
            : "Fill in the blanks to make the matrix symmetric. Remember: A[i][j] = A[j][i] in all positions!",
        matrix: [
            [5, 8, null, 2],
            [8, 10, 6, null],
            [3, 6, 12, null],
            [2, 4, 7, 15]
        ],
        correctMatrix: [
            [5, 8, 3, 2],
            [8, 10, 6, 4],
            [3, 6, 12, 7],
            [2, 4, 7, 15]
        ]
    },

    {
        id: 6,
        phase: 3,
        type: "symmetric",
        title: selLanguage
            ? "Συμμετρία 5×5 - Τελική Πρόκληση"
            : "5×5 Symmetry — Final Challenge",
        description: selLanguage
            ? "Η τελική δοκιμασία! Ολοκλήρωσε τον συμμετρικό πίνακα 5×5 συμπληρώνοντας όλα τα κενά."
            : "The final challenge! Complete the 5×5 symmetric matrix by filling all missing values.",
        matrix: [
            [20, null, 5, null, 8],
            [12, 18, null, 9, null],
            [5, 6, 25, null, 14],
            [3, 9, 11, 22, null],
            [8, 15, 14, 16, 30]
        ],
        correctMatrix: [
            [20, 12, 5, 3, 8],
            [12, 18, 6, 9, 15],
            [5, 6, 25, 11, 14],
            [3, 9, 11, 22, 16],
            [8, 15, 14, 16, 30]
        ]
    }
];


    useEffect(() => {
        resetLevel();
    }, [currentLevel]);

    useEffect(() => {
        if (lives <= 0) {
            setShowGameOver(true);
        }
    }, [lives]);

    const resetLevel = () => {
        const level = LEVELS[currentLevel];
        if (level.phase === 2) {
            setMatrix(level.template.map(row => [...row]));
        } else {
            setMatrix(level.matrix.map(row => [...row]));
        }
        setSelectedCell(null);
        setFoundErrors([]);
        setInputValue("");
        setMessage("");
        setShowHint(false);
    };

    const resetGame = () => {
        setCurrentLevel(0);
        setLives(3);
        setShowGameOver(false);
        setShowGameWon(false);
        resetLevel();
    };

    const handleCellClick = (rowIdx, colIdx) => {
        setSelectedCell({ row: rowIdx, col: colIdx });
        const level = LEVELS[currentLevel];

        if (level.phase === 2) {
            setInputValue("");
        } else if (level.phase === 3) {
            if (level.matrix[rowIdx][colIdx] === null) {
                setInputValue("");
            }
        }
    };

    const checkPhase1 = () => {
        const level = LEVELS[currentLevel];
        if (!selectedCell) {
            setMessage(selLanguage?"Επίλεξε ένα κελί!":"Select a cell!");
            setTimeout(() => setMessage(""), 2000);
            return;
        }

        const { row, col } = selectedCell;
        const isError = level.errors.some(err => err.row === row && err.col === col);
        const alreadyFound = foundErrors.some(err => err.row === row && err.col === col);

        if (alreadyFound) {
            setMessage(selLanguage?"Αυτό το λάθος το έχεις ήδη βρει!":" You've already found this error!");
            setTimeout(() => setMessage(""), 2000);
            return;
        }

        if (isError) {
            const newFoundErrors = [...foundErrors, { row, col }];
            setFoundErrors(newFoundErrors);
            setMessage(selLanguage?" Σωστό! Βρήκες ένα λάθος!":" Correct! You found an error!");
            setIsCorrect(true);

            if (newFoundErrors.length === level.errors.length) {
                setTimeout(() => {
                    setMessage(selLanguage?" Βρήκες όλα τα λάθη! Προχωράς στο επόμενο επίπεδο!":" You found all errors! You advance to the next level!");
                    setIsCorrect(true);
                    setTimeout(() => {
                        if (currentLevel < LEVELS.length - 1) {
                            setCurrentLevel(currentLevel + 1);
                        } else {
                            setShowGameWon(true);
                        }
                    }, 1500);
                }, 500);
            } else {
                setTimeout(() => setMessage(""), 2000);
            }
        } else {
            setMessage(selLanguage?"Λάθος! Αυτό το κελί είναι σωστό.":" Wrong! This cell is correct.");
            setIsCorrect(false);
            setLives(lives - 1);
            setTimeout(() => setMessage(""), 2000);
        }
    };

    const handlePhase2Input = () => {
        const level = LEVELS[currentLevel];
        if (!selectedCell) {
            setMessage(selLanguage?"Επίλεξε ένα κελί!":"Select a cell!");
            setTimeout(() => setMessage(""), 2000);
            return;
        }

        const value = inputValue === "" ? 0 : parseInt(inputValue);
        if (isNaN(value)) {
            setMessage(selLanguage?"Εισήγαγε έγκυρο αριθμό!":"Enter a valid number!");
            setIsCorrect(false);
            setTimeout(() => setMessage(""), 2000);
            return;
        }

        const { row, col } = selectedCell;
        const newMatrix = matrix.map(r => [...r]);
        newMatrix[row][col] = value;
        setMatrix(newMatrix);
        setInputValue("");
        setSelectedCell(null);
    };

    const checkPhase2 = () => {
        const level = LEVELS[currentLevel];
        let isValid = true;
        let errorMsg = "";

        for (let i = 0; i < level.size; i++) {
            for (let j = 0; j < level.size; j++) {
                if (matrix[i][j] === null) {
                    errorMsg = selLanguage?"Συμπλήρωσε όλα τα κελιά!":" Fill in all cells!";
                    isValid = false;
                    break;
                }

                if (level.type === "diagonal") {
                    if (i !== j && matrix[i][j] !== 0) {
                        errorMsg = selLanguage?`Το κελί [${i},${j}] πρέπει να είναι 0 (εκτός διαγωνίου)!`:` Cell [${i},${j}] must be 0 (off the diagonal)!`;
                        isValid = false;
                        break;
                    }
                    if (i === j && matrix[i][j] === 0) {
                        errorMsg = selLanguage?`Το κελί [${i},${j}] δεν πρέπει να είναι 0 (στη διαγώνιο)!`:` Cell [${i},${j}] must not be 0 (on the diagonal)!`;
                        isValid = false;
                        break;
                    }
                } else if (level.type === "lower_triangular") {
                    if (i < j && matrix[i][j] !== 0) {
                        errorMsg = selLanguage?`Το κελί [${i},${j}] πρέπει να είναι 0 (πάνω από διαγώνιο)!`:` Cell [${i},${j}] must be 0 (above diagonal)!`;
                        isValid = false;
                        break;
                    }
                } else if (level.type === "upper_triangular") {
                    if (i > j && matrix[i][j] !== 0) {
                        errorMsg = selLanguage?`Το κελί [${i},${j}] πρέπει να είναι 0 (κάτω από διαγώνιο)!`:` Cell [${i},${j}] must be 0 (below diagonal)!`;
                        isValid = false;
                        break;
                    }
                }
            }
            if (!isValid) break;
        }

        if (isValid) {
            setMessage(selLanguage?" Σωστός πίνακας! Προχωράς στο επόμενο επίπεδο!":" Correct matrix! You advance to the next level!");
            setIsCorrect(true);
            setTimeout(() => {
                if (currentLevel < LEVELS.length - 1) {
                    setCurrentLevel(currentLevel + 1);
                } else {
                    setShowGameWon(true);
                }
            }, 1500);
        } else {
            setMessage(errorMsg);
            setIsCorrect(false);
            setLives(lives - 1);
            setTimeout(() => setMessage(""), 3000);
        }
    };

    const handlePhase3Input = () => {
        const level = LEVELS[currentLevel];
        if (!selectedCell) {
            setMessage(selLanguage?"Επίλεξε ένα κενό κελί!":"Select an empty cell!");
            setTimeout(() => setMessage(""), 2000);
            return;
        }

        const { row, col } = selectedCell;
        if (level.matrix[row][col] !== null) {
            setMessage(selLanguage?"Αυτό το κελί είναι ήδη συμπληρωμένο!":" This cell is already filled!");
            setIsCorrect(false);
            setTimeout(() => setMessage(""), 2000);
            return;
        }

        const value = parseInt(inputValue);
        if (isNaN(value)) {
            setMessage(selLanguage?"Εισήγαγε έγκυρο αριθμό!":" Enter a valid number!");
            setIsCorrect(false);
            setTimeout(() => setMessage(""), 2000);
            return;
        }

        const newMatrix = matrix.map(r => [...r]);
        newMatrix[row][col] = value;
        setMatrix(newMatrix);
        setInputValue("");
        setSelectedCell(null);
    };

    const checkPhase3 = () => {
        const level = LEVELS[currentLevel];
        let isValid = true;
        let errorMsg = "";

        for (let i = 0; i < matrix.length; i++) {
            for (let j = 0; j < matrix[i].length; j++) {
                if (matrix[i][j] === null) {
                    errorMsg = selLanguage?"Συμπλήρωσε όλα τα κενά κελιά!": " Fill in all empty cells!";
                    isValid = false;
                    break;
                }
                if (matrix[i][j] !== matrix[j][i]) {
                    errorMsg = selLanguage?`Ο πίνακας δεν είναι συμμετρικός! A[${i}][${j}] ≠ A[${j}][${i}]`: ` Matrix is not symmetric! A[${i}][${j}] ≠ A[${j}][${i}]`;
                    isValid = false;
                    break;
                }
            }
            if (!isValid) break;
        }

        if (isValid) {
            setMessage(selLanguage?" Σωστός συμμετρικός πίνακας! Προχωράς στο επόμενο επίπεδο!":" Correct symmetric matrix! You advance to the next level!");
            setIsCorrect(true);
            setTimeout(() => {
                if (currentLevel < LEVELS.length - 1) {
                    setCurrentLevel(currentLevel + 1);
                } else {
                    setShowGameWon(true);
                }
            }, 1500);
        } else {
            setMessage(errorMsg);
            setIsCorrect(false);
            setLives(lives - 1);
            setTimeout(() => setMessage(""), 3000);
        }
    };

    const getHintText = () => {
        const level = LEVELS[currentLevel];
        if (level.phase === 1) {
            if (level.type === "diagonal") {
                return selLanguage?"💡 Hint: Σε διαγώνιο πίνακα, μόνο τα στοιχεία A[i][i] μπορούν να είναι μη-μηδενικά.":"💡 Hint: In a diagonal matrix, only the elements A[i][i] can be non-zero."
            } else if (level.type === "upper_triangular") {
                return selLanguage?"💡 Hint: Σε άνω τριγωνικό, όλα τα A[i][j] με i > j πρέπει να είναι 0.":"💡 Hint: In upper triangular, all A[i][j] with i > j must be 0.";
            }
        } else if (level.phase === 3) {
            return selLanguage?"💡 Hint: Συμμετρικός πίνακας σημαίνει A[i][j] = A[j][i] για κάθε i, j.":"💡 Hint: Symmetric matrix means A[i][j] = A[j][i] for every i, j.";
        }
        return "";
    };

    const isCellSelected = (rowIdx, colIdx) => {
        return selectedCell && selectedCell.row === rowIdx && selectedCell.col === colIdx;
    };

    const isCellError = (rowIdx, colIdx) => {
        return foundErrors.some(err => err.row === rowIdx && err.col === colIdx);
    };

    const isCellEditable = (rowIdx, colIdx) => {
        const level = LEVELS[currentLevel];
        if (level.phase === 2) return true;
        if (level.phase === 3) return level.matrix[rowIdx][colIdx] === null;
        return false;
    };

    const level = LEVELS[currentLevel];
    const size = matrix.length;

    return (
        <div
            className="main-content special-matrices-container"
            style={{
                background: darkMode ? "#1E293B" : "var(--customBuildButton)",
            }}
        >
            <GameOverModal isVisible={showGameOver} onPlayAgain={resetGame} />
            <GameWonModal isVisible={showGameWon} onPlayAgain={resetGame} />

            <div style={{ width: "100%", maxWidth: "1100px" }}>
                {/* Lives Display */}


                <div style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "15px",
                    flexWrap: "wrap",
                    gap: "10px"
                }}>

                    <div style={{
                        color: darkMode ? "white" : "#1e293b",
                        padding: "12px 24px",
                        borderRadius: "12px",
                        fontWeight: "700",
                        fontSize: "18px",
                        border: darkMode ? "2px solid rgba(255, 255, 255, 0.2)" : "2px solid #1e293b",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px"
                    }}>
                        {selLanguage?`Επίπεδο ${currentLevel + 1} / ${LEVELS.length}`:`Level ${currentLevel + 1} / ${LEVELS.length}`}
                        </div>


                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "row",
                        gap: "12px"
                    }}>

                        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                            {[1, 2, 3].map((heartNum) => (
                                <div
                                    key={heartNum}
                                    style={{
                                        fontSize: "24px",
                                        filter: lives >= heartNum
                                            ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                            : "grayscale(100%) opacity(0.3)",
                                        transform: lives >= heartNum ? "scale(1)" : "scale(0.8)",
                                        transition: "all 0.3s ease"
                                    }}
                                >
                                    ❤️
                                </div>
                            ))}
                        </div>


                    </div>
                </div>

                {/* Level Info */}
                <div className="special-matrices-prom">
                    <div>

                        <p style={{ margin: "0", fontSize: "0.95rem" }}>{level.description}</p>
                    </div>
                </div>

                {/* Matrix Display */}
                <div className="special-matrices-frame" style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "40px 20px",
                    overflowX: "auto",
                    overflowY: "auto"
                }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        {/* Column Numbers */}
                        <div style={{ display: "flex", marginBottom: "10px", marginLeft: "60px" }}>
                            {Array.from({ length: size }).map((_, colIdx) => (
                                <div
                                    key={`col-label-${colIdx}`}
                                    style={{
                                        width: "80px",
                                        marginRight: "10px",
                                        textAlign: "center",
                                        fontWeight: "700",
                                        fontSize: "1rem",
                                        color: "#3b82f6"
                                    }}
                                >
                                    {colIdx}
                                </div>
                            ))}
                        </div>

                        {/* Matrix with Row Numbers */}
                        {matrix.map((row, rowIdx) => (
                            <div key={`row-${rowIdx}`} style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                                <div style={{
                                    width: "50px",
                                    textAlign: "center",
                                    fontWeight: "700",
                                    fontSize: "1rem",
                                    color: "#3b82f6",
                                    marginRight: "10px"
                                }}>
                                    {rowIdx}
                                </div>

                                <div style={{ display: "flex", gap: "10px" }}>
                                    {row.map((value, colIdx) => (
                                        <motion.div
                                            key={`${rowIdx}-${colIdx}`}
                                            onClick={() => handleCellClick(rowIdx, colIdx)}
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.95 }}
                                            style={{
                                                width: size >= 5 ? "70px" : "80px",
                                                height: size >= 5 ? "70px" : "80px",
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                fontSize: size >= 5 ? "1.5rem" : "1.8rem", fontWeight: "700",
                                                color: "white",
                                                background: isCellError(rowIdx, colIdx)
                                                    ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                                                    : isCellSelected(rowIdx, colIdx)
                                                        ? "linear-gradient(135deg, #f97316 0%, #ea580c 100%)"
                                                        : value === null
                                                            ? "linear-gradient(135deg, #94a3b8 0%, #64748b 100%)"
                                                            : rowIdx === colIdx
                                                                ? "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)"
                                                                : "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                                                borderRadius: "12px",
                                                cursor: "pointer",
                                                boxShadow: isCellSelected(rowIdx, colIdx)
                                                    ? "0 0 20px rgba(249, 115, 22, 0.6)"
                                                    : "0 4px 12px rgba(0, 0, 0, 0.15)",
                                                border: isCellSelected(rowIdx, colIdx)
                                                    ? "3px solid #fbbf24"
                                                    : isCellEditable(rowIdx, colIdx)
                                                        ? "2px dashed rgba(255,255,255,0.5)"
                                                        : "none",
                                                transition: "all 0.2s ease"
                                            }}
                                        >
                                            {value === null ? "?" : value}
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Message Display */}
                <AnimatePresence>
                    {message && (
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            style={{
                                textAlign: "center",
                                padding: "15px 20px",
                                borderRadius: "12px",
                                marginTop: "20px",
                                background: isCorrect
                                    ? "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)"
                                    : "linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)",
                                color: isCorrect ? "#059669" : "#dc2626",
                                fontWeight: "600",
                                fontSize: "1rem",
                                boxShadow: isCorrect
                                    ? "0 0 20px rgba(16, 185, 129, 0.4)"
                                    : "0 0 20px rgba(220, 38, 38, 0.4)"
                            }}
                        >
                            {message}
                        </motion.div>
                    )}
                </AnimatePresence>

                {(level.phase === 2 || level.phase === 3) && selectedCell && (
                    <div style={{
                        textAlign: "center",
                        marginTop: "20px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "12px"
                    }}>
                        <div style={{
                            color: "white",
                            fontSize: "1rem",
                            fontWeight: "600"
                        }}>
                            {selLanguage?`Επιλεγμένο κελί: [${selectedCell.row}, ${selectedCell.col}]`:` Selected Cell: [${selectedCell.row}, ${selectedCell.col}]`}
                        </div>
                        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                            <input
                                type="number"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                placeholder="Αριθμός..."
                                style={{
                                    padding: "12px 20px",
                                    fontSize: "1rem",
                                    borderRadius: "12px",
                                    border: "2px solid #3b82f6",
                                    outline: "none",
                                    width: "150px",
                                    textAlign: "center",
                                    fontWeight: "600"
                                }}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        if (level.phase === 2) handlePhase2Input();
                                        else if (level.phase === 3) handlePhase3Input();
                                    }
                                }}
                            />
                            <button
                                onClick={level.phase === 2 ? handlePhase2Input : handlePhase3Input}
                                style={{
                                    background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "12px",
                                    padding: "12px 24px",
                                    fontSize: "1rem",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    boxShadow: "0 8px 20px rgba(139, 92, 246, 0.3)",
                                    transition: "all 0.3s ease"
                                }}
                            >
                                {selLanguage?"Εισαγωγή":"Submit"}
                            </button>
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <div style={{
                    textAlign: "center",
                    marginTop: "30px",
                    display: "flex",
                    gap: "12px",
                    justifyContent: "center",
                    flexWrap: "wrap"
                }}>
                    {level.phase === 1 && (
                        <button
                            onClick={checkPhase1}
                            style={{
                                background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                                color: "white",
                                border: "none",
                                borderRadius: "16px",
                                padding: "14px 28px",
                                fontSize: "1rem",
                                fontWeight: 600,
                                cursor: "pointer",
                                boxShadow: "0 10px 25px rgba(68, 85, 239, 0.3)",
                                transition: "all 0.3s ease"
                            }}
                        >
                            {selLanguage?"Σημείωσε ως Λάθος":"Mark as Error"}
                        </button>
                    )}

                    {level.phase === 2 && (
                        <button
                            onClick={checkPhase2}
                            style={{
                                background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                                color: "white",
                                border: "none",
                                borderRadius: "16px",
                                padding: "14px 28px",
                                fontSize: "1rem",
                                fontWeight: 600,
                                cursor: "pointer",
                                boxShadow: "0 10px 25px rgba(16, 41, 185, 0.3)",
                                transition: "all 0.3s ease"
                            }}
                        >
                            {selLanguage?"Έλεγχος Πίνακα":"Check Matrix"}
                        </button>
                    )}

                    {level.phase === 3 && (
                        <button
                            onClick={checkPhase3}
                            style={{
                                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                color: "white",
                                border: "none",
                                borderRadius: "16px",
                                padding: "14px 28px",
                                fontSize: "1rem",
                                fontWeight: 600,
                                cursor: "pointer",
                                boxShadow: "0 10px 25px rgba(16, 185, 129, 0.3)",
                                transition: "all 0.3s ease"
                            }}
                        >
                            {selLanguage?"✓ Έλεγχος Συμμετρίας":"✓ Check Symmetry"}
                        </button>
                    )}

                    <button
                        onClick={resetLevel}
                        style={{
                            background: "linear-gradient(135deg, #b81e1eff 0%, #7c0505ff 100%)",
                            color: "white",
                            border: "none",
                            borderRadius: "16px",
                            padding: "14px 28px",
                            fontSize: "1rem",
                            fontWeight: 600,
                            cursor: "pointer",
                            boxShadow: "0 10px 25px rgba(184, 30, 30, 0.3)",
                            transition: "all 0.3s ease"
                        }}
                    >
                        {selLanguage?"Επαναφορά Επιπέδου":"Reset Level"}    
                    </button>
                </div>
            </div>
        </div>
    );
};

export default VarietyOfArrays;